import { useTranslation } from 'react-i18next';

import { AxiosError } from 'axios';

import { CustomModal } from 'components/shared';
import { useCostTypeLabels } from 'constants/costTypeLabels';
import { staleChargesErrors } from 'constants/errorCodes';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useModal } from 'hooks/useModal';
import { Charge, OrderDTO } from 'interfaces';
import { HTTPService } from 'service';
import { logNetworkError } from 'utils/logNetworkError';

import styles from './CancelAdditionalChargeModal.module.css';

type CancelAdditionalChargeModalProps = {
   orderId: string;
   chargeToCancel: { charge: Charge; chargeIndex: number } | null;
   onSuccessCancelCharge: (updatedOrder: OrderDTO) => void;
   onStaleCharges: (errorDescription: string) => void;
   opened: boolean;
   onClose: () => void;
};

const CancelAdditionalChargeModal = ({
   orderId,
   chargeToCancel,
   onSuccessCancelCharge,
   onStaleCharges,
   opened,
   onClose,
}: CancelAdditionalChargeModalProps) => {
   const { t } = useTranslation();
   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose,
   });

   const { costTypeLabels } = useCostTypeLabels();
   const { CANCEL_ADDITIONAL_CHARGE_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleCancelCharge = () => {
      if (!chargeToCancel) {
         return;
      }
      const { charge, chargeIndex } = chargeToCancel;
      setIsLoading(true);
      HTTPService.cancelAdditionalCharge(orderId, {
         chargeIndex,
         chargeType: charge.chargeType,
         palletType: charge.palletType,
         totalAmount: charge.totalAmount,
      })
         .then(response => {
            onSuccessCancelCharge(response.data);
            closeModalHandler();
         })
         .catch(error => {
            const errorCode = error instanceof AxiosError ? error.response?.data?.code : undefined;
            if (errorCode && staleChargesErrors.includes(errorCode)) {
               // the displayed charges are out of date - the parent refetches the whole order
               onStaleCharges(CANCEL_ADDITIONAL_CHARGE_ERROR_DESCRIPTIONS[errorCode]);
               closeModalHandler();
            } else {
               setErrorInfo(
                  (errorCode && CANCEL_ADDITIONAL_CHARGE_ERROR_DESCRIPTIONS[errorCode]) ||
                     CANCEL_ADDITIONAL_CHARGE_ERROR_DESCRIPTIONS.OTHER,
               );
            }
            logNetworkError(error);
         })
         .finally(() => setIsLoading(false));
   };

   const chargeLabel =
      chargeToCancel &&
      (chargeToCancel.charge.otherChargeName || costTypeLabels[chargeToCancel.charge.chargeType]);

   return (
      <CustomModal
         opened={opened}
         onClose={closeModalHandler}
         isLoading={isLoading}
         error={errorInfo}
         title={t('charges.deleteModal.title')}
         innerTitle={t('common.warning')}
         icon="ALERT_TRIANGLE"
         primaryButtonProps={{
            text: t('common.delete'),
            variant: 'filled-danger',
            onClick: handleCancelCharge,
         }}
         secondaryButtonProps={{
            text: t('common.back'),
            variant: 'text',
            onClick: closeModalHandler,
         }}
         size={670}
         modalContentClassName={styles.modalContent}
      >
         <p>{t('charges.deleteModal.confirm', { chargeName: chargeLabel })}</p>
         <p>
            {chargeToCancel?.charge.chargeType === 'OTHER'
               ? t('charges.deleteModal.otherChargeConsequence')
               : t('charges.deleteModal.automaticChargeConsequence')}
         </p>
      </CustomModal>
   );
};

export default CancelAdditionalChargeModal;
